import { Inject, Injectable } from '@angular/core';
import { EmailManagementApiService, EmailManagementSettings, LastRefreshResponse, PmsSyncStatus } from '@anduin/api';
import { getLoadableGetterAction, Loadable, LoadableStatus, setLoadableInState } from '@anduin/shared';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { FAILED_FILTERS_KEY, PENDING_FILTERS_KEY, SENT_FILTERS_KEY } from '../utils/session-filters-key';
import {
  LoadLastRefresh,
  LoadMetrics,
  LoadPmsSyncStatus,
  LoadSettings,
  SetActiveTab,
  UpdateFailedFilters,
  UpdatePmsSyncPollingState,
  UpdateSentFilters,
  UpdatePendingFilters,
} from './email-delivery-hub.actions';
import { EMAIL_DELIVERY_HUB_DEFAULT_STATE, EmailDeliveryHubModel, EmailDeliveryHubSentMetrics, PmsSyncPollingState } from './email-delivery-hub.model';
import { EmailDeliveryTab } from '../models/email-delivery-tab.enum';
// avoid circular dependencies (honestly should be a TODO to reorganize things to avoid this)
import { EmailDeliveryHubMetricsService } from '../components/email-delivery-hub-insights/services/email-delivery-hub-metrics.service';
import { KEY_VALUE_STORAGE, KeyValueStorage } from '../services/key-value-storage';
import { EmailDeliveryHubFailedFilters, EmailDeliveryHubPendingFilters, EmailDeliveryHubSentFilters } from '../models/email-delivery-hub-filters.models';

@State<EmailDeliveryHubModel>({
  name: 'emailDeliveryHub',
  defaults: EMAIL_DELIVERY_HUB_DEFAULT_STATE,
})
@Injectable({
  providedIn: 'root',
})
export class EmailDeliveryHubState {
  constructor(
    @Inject(KEY_VALUE_STORAGE) private readonly keyValueStorage: KeyValueStorage,
    private readonly emailDeliveryHubMetricsService: EmailDeliveryHubMetricsService,
    private readonly emailManagementApiService: EmailManagementApiService,
    private readonly store: Store,
  ) {}

  @Action([UpdateSentFilters])
  updateFilters(ctx: StateContext<EmailDeliveryHubModel>, { filters }: UpdateSentFilters): void {
    this.keyValueStorage.set(SENT_FILTERS_KEY, JSON.stringify(filters));

    ctx.patchState({ sentFilters: { status: LoadableStatus.LOADED, data: { ...filters } } });
  }

  @Action([UpdatePendingFilters])
  updatePendingFilters(ctx: StateContext<EmailDeliveryHubModel>, { filters }: UpdatePendingFilters): void {
    this.keyValueStorage.set(PENDING_FILTERS_KEY, JSON.stringify(filters));
    ctx.patchState({ pendingFilters: { status: LoadableStatus.LOADED, data: filters } });
  }

  @Action([UpdateFailedFilters])
  updateFailedFilters(ctx: StateContext<EmailDeliveryHubModel>, { filters }: UpdateFailedFilters): void {
    this.keyValueStorage.set(FAILED_FILTERS_KEY, JSON.stringify(filters));
    ctx.patchState({ failedFilters: { status: LoadableStatus.LOADED, data: filters } });
  }

  @Action([LoadMetrics])
  loadMetrics(ctx: StateContext<EmailDeliveryHubModel>) {
    ctx.patchState({
      sentMetrics: { status: LoadableStatus.LOADING },
    });

    const filters = ctx.getState().sentFilters?.data ?? {};
    return this.emailDeliveryHubMetricsService.loadSentMetrics(filters).pipe(setLoadableInState(ctx.patchState, 'sentMetrics'));
  }

  @Action([LoadLastRefresh])
  loadLastRefresh(ctx: StateContext<EmailDeliveryHubModel>, { forceRefresh }: LoadLastRefresh): Observable<unknown> {
    return getLoadableGetterAction(
      this.store,
      EmailDeliveryHubState.getLastRefresh,
      forceRefresh,
      ctx,
      'lastRefresh',
      this.emailManagementApiService.getLastRefresh(),
    );
  }

  @Action([LoadSettings])
  loadSettings(ctx: StateContext<EmailDeliveryHubModel>, { forceRefresh }: LoadSettings): Observable<unknown> {
    return getLoadableGetterAction(
      this.store,
      EmailDeliveryHubState.getSettingsLoadable,
      forceRefresh,
      ctx,
      'settings',
      this.emailManagementApiService.getSettings(),
    );
  }

  @Action([UpdatePmsSyncPollingState])
  updatePmsSyncPollingState(ctx: StateContext<EmailDeliveryHubModel>, { state }: UpdatePmsSyncPollingState): void {
    ctx.patchState({ pmsSyncPollingState: state });
  }

  @Action([SetActiveTab])
  setActiveTab(ctx: StateContext<EmailDeliveryHubModel>, { tab }: SetActiveTab): void {
    ctx.patchState({ activeTab: tab });
  }

  @Action([LoadPmsSyncStatus])
  loadPmsSyncStatus(ctx: StateContext<EmailDeliveryHubModel>, { forceRefresh }: LoadPmsSyncStatus): Observable<unknown> {
    return getLoadableGetterAction(
      this.store,
      EmailDeliveryHubState.getPmsSyncStatus,
      forceRefresh,
      ctx,
      'pmsSyncStatus',
      this.emailManagementApiService.getPmsSyncStatus(),
    );
  }

  @Selector()
  static getFiltersLoadable(state: EmailDeliveryHubModel): Loadable<Partial<EmailDeliveryHubSentFilters>> {
    return state.sentFilters;
  }

  @Selector()
  static getFilters(state: EmailDeliveryHubModel): Partial<EmailDeliveryHubSentFilters> {
    return state.sentFilters?.data ?? {};
  }

  @Selector()
  static getMetrics(state: EmailDeliveryHubModel): Loadable<EmailDeliveryHubSentMetrics> {
    return state.sentMetrics;
  }

  @Selector()
  static getBouncedCount(state: EmailDeliveryHubModel): number {
    return state.sentMetrics?.data?.bounced ?? 0;
  }

  @Selector()
  static getLastRefresh(state: EmailDeliveryHubModel): Loadable<LastRefreshResponse> {
    return state.lastRefresh;
  }

  @Selector()
  static getLastRefreshStatus(state: EmailDeliveryHubModel): LoadableStatus {
    return state.lastRefresh?.status;
  }

  @Selector()
  static getSettingsLoadable(state: EmailDeliveryHubModel): Loadable<EmailManagementSettings> {
    return state.settings;
  }

  @Selector()
  static getSettings(state: EmailDeliveryHubModel): EmailManagementSettings | undefined {
    return state.settings?.data;
  }

  @Selector()
  static getSettingsStatus(state: EmailDeliveryHubModel): LoadableStatus {
    return state.settings?.status;
  }

  @Selector()
  static getPendingFilters(state: EmailDeliveryHubModel): EmailDeliveryHubPendingFilters {
    return state.pendingFilters?.data
      ?? {
        dateRange: { min: undefined, max: undefined },
        search: '',
        showDismissedAndResolved: false,
      };
  }

  @Selector()
  static getFailedFilters(state: EmailDeliveryHubModel): EmailDeliveryHubFailedFilters {
    return state.failedFilters?.data
      ?? {
        dateRange: { min: undefined, max: undefined },
        search: '',
        showDismissedAndResolved: false,
      };
  }

  @Selector()
  static getPmsSyncStatus(state: EmailDeliveryHubModel): Loadable<PmsSyncStatus> {
    return state.pmsSyncStatus;
  }

  @Selector()
  static getPmsSyncPollingState(state: EmailDeliveryHubModel): PmsSyncPollingState {
    return state.pmsSyncPollingState;
  }

  @Selector()
  static getActiveTab(state: EmailDeliveryHubModel): EmailDeliveryTab {
    return state.activeTab;
  }
}
